import { CurrencyInput, Input, formatCurrency } from '@hue/design-system';
import type { Currency, ServiceUnitTerms } from '../../lib/api.js';

export interface ServiceUnit {
  id: string;
  displayName: string;
}

/// Preço e duração próprios por estabelecimento (ADR 0074). Campo vazio usa o do serviço.
export function ServiceUnitTermsFields({
  units,
  value,
  onChange,
  currency,
  basePriceCents,
  durationMinutes,
  disabled,
}: {
  units: readonly ServiceUnit[];
  value: readonly ServiceUnitTerms[];
  onChange: (value: ServiceUnitTerms[]) => void;
  currency: Currency;
  basePriceCents: number;
  durationMinutes: number;
  disabled?: boolean | undefined;
}) {
  function change(businessUnitId: string, patch: Partial<ServiceUnitTerms>) {
    const current = value.find((terms) => terms.businessUnitId === businessUnitId) ?? {
      businessUnitId,
      priceCents: null,
      durationMinutes: null,
    };
    onChange([
      ...value.filter((terms) => terms.businessUnitId !== businessUnitId),
      { ...current, ...patch },
    ]);
  }

  return (
    <fieldset className="grid gap-3 sm:col-span-2">
      <legend className="mb-1 text-sm font-semibold">
        Preço e duração por unidade{' '}
        <span className="font-normal text-content-muted">· opcional</span>
      </legend>
      <p className="text-xs leading-5 text-content-muted">
        Deixe em branco para usar o preço-base e a duração do serviço. Vale para novos agendamentos
        e vendas daquela unidade; o que já foi marcado ou vendido não muda.
      </p>
      <ul className="grid gap-2">
        {units.map((unit) => {
          const terms = value.find((item) => item.businessUnitId === unit.id);
          return (
            <li
              key={unit.id}
              className="grid gap-3 rounded-xl border border-surface-border px-3 py-3 sm:grid-cols-[minmax(0,1fr)_10rem_9rem] sm:items-end"
            >
              <span className="text-sm font-medium sm:pb-3">{unit.displayName}</span>
              <CurrencyInput
                label={`Preço em ${unit.displayName}`}
                currency={currency}
                value={terms?.priceCents ?? null}
                hint={`Vazio: ${formatCurrency(basePriceCents, currency)}`}
                onValueChange={(priceCents) => change(unit.id, { priceCents })}
                disabled={disabled}
              />
              <Input
                label={`Minutos em ${unit.displayName}`}
                type="number"
                inputMode="numeric"
                min={5}
                max={1440}
                step={5}
                hint={`Vazio: ${durationMinutes} min`}
                value={terms?.durationMinutes ?? ''}
                onChange={(event) =>
                  change(unit.id, {
                    durationMinutes: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
                disabled={disabled}
              />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
